import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';

import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css'; 
import { 
  FileText, Plus, Trash2, Save, 
  UploadCloud, Loader2, Globe 
} from 'lucide-react';

// --- Reuse Image Uploader Logic ---
const BlogImageUploader = ({ value, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axiosClient.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChange(res.url); 
        } catch (err) { alert("Upload failed"); } finally { setUploading(false); }
    };
    return (
        <div className="relative group border-2 border-dashed border-slate-300 rounded-xl h-48 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer overflow-hidden">
             {value ? (
                 <>
                   <img src={value} alt="Featured" className="w-full h-full object-cover" />
                   <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white font-bold">Change Image</div>
                 </>
             ) : (
                 <>
                   {uploading ? <Loader2 className="w-8 h-8 animate-spin text-teal-500"/> : <UploadCloud className="w-8 h-8 text-slate-400 mb-2" />}
                   <span className="text-xs text-slate-500 font-bold uppercase">Upload Cover Image</span>
                 </>
             )}
             <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileChange} accept="image/*" />
        </div>
    );
};

const BlogManager = () => {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  
  const initialForm = {
      title: '', subtitle: '', slug: '', category: 'Security', 
      content_html: '', featured_image_url: '', is_published: false
  };
  const [formData, setFormData] = useState(initialForm);

  // Fetch Blogs (SAFE MODE)
  const { data: rawData } = useQuery({
    queryKey: ['adminBlogs'],
    queryFn: async () => {
        try {
            return await axiosClient.get('/admin/cms/blogs');
        } catch (e) {
            console.error("Failed to fetch blogs", e);
            return [];
        }
    }
  });

  // Ensure it is always an array
  const blogs = Array.isArray(rawData) ? rawData : [];

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/cms/blogs', data),
    onSuccess: () => {
        queryClient.invalidateQueries(['adminBlogs']);
        alert("Blog Post Saved! 📝");
        if (!selectedId) setFormData(initialForm); 
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/cms/blogs/${id}`),
    onSuccess: (data, deletedId) => {
        queryClient.invalidateQueries(['adminBlogs']);
        if (selectedId === deletedId) { 
            setSelectedId(null); 
            setFormData(initialForm); 
        }
    }
  });

  // Handle Selection
  const handleSelect = (blog) => {
      setSelectedId(blog.id);
      setFormData(blog);
  };

  const handleNew = () => {
      setSelectedId(null);
      setFormData(initialForm);
  };

  // Auto-Generate Slug from Title
  const handleTitleChange = (e) => {
      const title = e.target.value;
      const slug = !selectedId ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : formData.slug;
      
      setFormData(prev => ({
          ...prev, 
          title,
          slug
      }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)] animate-in fade-in slide-in-from-bottom-4">
       
       {/* LEFT: Sidebar List */}
       <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-sm">
           <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
               <span className="font-bold text-slate-700 flex items-center"><FileText className="w-4 h-4 mr-2"/> All Posts</span>
               <button onClick={handleNew} className="bg-blue-600 text-white p-1.5 rounded-lg hover:bg-blue-700 transition-colors"><Plus className="w-4 h-4"/></button>
           </div>
           <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
               {blogs.length === 0 && <p className="text-center text-xs text-slate-400 mt-10">No blogs yet.</p>}
               {blogs.map(blog => (
                   <div 
                     key={blog.id} 
                     onClick={() => handleSelect(blog)}
                     className={`p-3 rounded-lg cursor-pointer transition-all border ${selectedId === blog.id ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-transparent hover:bg-slate-50'}`}
                   >
                       <h4 className={`text-sm font-bold truncate ${selectedId === blog.id ? 'text-teal-700' : 'text-slate-700'}`}>{blog.title}</h4>
                       <div className="flex justify-between items-center mt-2">
                           <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${blog.is_published ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                               {blog.is_published ? 'Live' : 'Draft'}
                           </span>
                           <button onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete this post?')) deleteMutation.mutate(blog.id); }} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3"/></button>
                       </div>
                   </div>
               ))}
           </div>
       </div>

       {/* RIGHT: Editor */}
       <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
           {/* Editor Toolbar */}
           <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
               <h2 className="font-bold text-slate-800 text-lg">{selectedId ? 'Edit Post' : 'Create New Post'}</h2>
               <div className="flex gap-3">
                   <label className="flex items-center cursor-pointer select-none bg-white border px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                       <input type="checkbox" className="mr-2" checked={formData.is_published} onChange={e => setFormData({...formData, is_published: e.target.checked})} />
                       <span className={`text-sm font-bold ${formData.is_published ? 'text-green-600' : 'text-slate-500'}`}>
                           {formData.is_published ? 'Published' : 'Draft Mode'}
                       </span>
                   </label>
                   <button 
                      onClick={() => saveMutation.mutate(formData)} 
                      disabled={saveMutation.isLoading}
                      className="bg-emerald-600 text-white px-5 py-1.5 rounded-lg font-bold flex items-center hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
                   >
                      <Save className="w-4 h-4 mr-2"/> {saveMutation.isLoading ? 'Saving...' : 'Save Post'}
                   </button>
               </div>
           </div>

           {/* Editor Content (Scrollable) */}
           <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
               
               <div className="grid grid-cols-3 gap-6">
                   <div className="col-span-2 space-y-4">
                       <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
                           <input className="w-full p-3 border rounded-lg text-lg font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Enter Blog Title..." value={formData.title} onChange={handleTitleChange} />
                       </div>
                       <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sub-Heading / Excerpt</label>
                           <textarea className="w-full p-3 border rounded-lg text-sm text-slate-600 h-20 resize-none focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Short summary for SEO..." value={formData.subtitle} onChange={e => setFormData({...formData, subtitle: e.target.value})} />
                       </div>
                   </div>
                   <div className="space-y-4">
                        <BlogImageUploader value={formData.featured_image_url} onChange={(url) => setFormData({...formData, featured_image_url: url})} />
                        <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                           <select className="w-full p-2 border rounded-lg bg-white" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                               <option value="Security">Security</option>
                               <option value="Updates">Product Updates</option>
                               <option value="Tutorials">Tutorials</option>
                               <option value="News">Cyber News</option>
                           </select>
                        </div>
                   </div>
               </div>

               {/* Slug Preview */}
               <div className="flex items-center text-xs text-slate-400 bg-slate-50 p-2 rounded border border-slate-100">
                   <Globe className="w-3 h-3 mr-2" />
                   https://airs-security.com/blog/<span className="text-teal-600 font-mono ml-1 font-bold">{formData.slug}</span>
               </div>

               {/* Rich Text Editor (Corrected) */}
               <div className="h-96 pb-12">
                   <ReactQuill 
                      theme="snow" 
                      value={formData.content_html} 
                      onChange={(val) => setFormData({...formData, content_html: val})} 
                      className="h-80"
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, false] }],
                          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                          [{'list': 'ordered'}, {'list': 'bullet'}],
                          ['link', 'image', 'code-block'],
                          ['clean']
                        ],
                      }}
                   />
               </div>
           </div>
       </div>

    </div>
  );
};

export default BlogManager;